/**
 * API Parameter Conversion Utilities
 *
 * Converts locale strings to the format required by different APIs
 */

import type { SaleorLanguageCodeEnum } from '@/graphql/saleor/api';
import { localeConfig } from './configuration';

export interface ApiParams {
  /** Saleor language code (e.g., 'EN_US', 'FR', 'DE') */
  languageCode: SaleorLanguageCodeEnum;

  /** Sales channel (e.g., 'us-usd', 'ca-cad') */
  channel: string;

  /** Contentful locale (e.g., 'en-US', 'fr', 'zh-Hans') */
  contentfulLocale: string;

  /** Country code (e.g., 'US', 'CA', 'GB') */
  countryCode: string;
}

/**
 * Convert URL locale to API parameters
 *
 * Centralizes the conversion logic used across the application
 * to avoid duplication and ensure consistency.
 *
 * @param lang - URL locale format (e.g., 'us-en', 'ca-fr', 'gb-en')
 * @returns API parameters for Saleor, Contentful, and OrderApp
 *
 * @example
 * const params = convertLocaleToApiParams('us-en');
 * // {
 * //   languageCode: 'EN_US',
 * //   channel: 'us-usd',
 * //   contentfulLocale: 'en-US',
 * //   countryCode: 'US'
 * // }
 */
export function convertLocaleToApiParams(lang: string): ApiParams {
  if (!localeConfig.isValidLocale(lang)) {
    throw new Error(`Invalid locale: ${lang}`);
  }

  const { locale, country } = localeConfig.parseLocale(lang);

  // Convert to Saleor format: Replace hyphen with underscore and uppercase
  // e.g., 'en-US' -> 'EN_US', 'fr' -> 'FR'
  const contentfulLocale = locale.contentful || 'en-US';
  const languageCode = contentfulLocale.replace('-', '_').toUpperCase() as SaleorLanguageCodeEnum;

  return {
    languageCode,
    channel: country.channelSlug,
    contentfulLocale,
    countryCode: country.code,
  };
}
